import React, { useState } from 'react'

import DateRangeCalendar from './DateRangeCalendar'
import { ArrowRightIcon, CalendarIcon } from './icons'
import { dayCount, formatDate, formatRange, monthOf, nextMonth, todayIso } from './dateRange.utils'

/**
 * "Start → End" range field (mock `.cev-range`, create.html `choose()`): one control for both
 * days, like a travel site. First click = start, second = end (the same day for a one-day event),
 * the range previews on hover. Clicking a day before the start restarts the range; clicking the
 * start half of a complete range moves its first day. Escape closes the calendar first (and stops
 * there: the dialog's own Escape only runs when the calendar is closed).
 */
export default function DateRangeField({
    start,
    end,
    onChange,
    invalid = false,
    labelledBy,
    describedBy,
    // Refs on the two halves: the form focuses them on error / after a pick.
    startRef,
    endRef,
    language,
    t,
}) {
    const [open, setOpen] = useState(false)
    const [pick, setPick] = useState('start')
    const [view, setView] = useState(() => monthOf(todayIso()))
    const [focusIso, setFocusIso] = useState('')
    const [focusKey, setFocusKey] = useState(0)

    const requestFocus = (iso) => {
        setFocusIso(iso)
        setFocusKey((key) => key + 1)
    }

    const openCalendar = (which) => {
        const today = todayIso()
        const nextPick = which === 'end' && start ? 'end' : 'start'
        const base = (nextPick === 'end' ? end || start : start) || today
        setPick(nextPick)
        setView(monthOf(base))
        setOpen(true)
        requestFocus(base >= today ? base : today)
    }

    const closeCalendar = () => setOpen(false)

    const handleHalf = (which) => {
        const target = which === 'end' && start ? 'end' : 'start'
        if (open && pick === target) closeCalendar()
        else openCalendar(which)
    }

    const choose = (iso) => {
        if (pick === 'end' && start && iso >= start) {
            // second click: the end, and the calendar closes
            onChange({ start, end: iso })
            setPick('start')
            closeCalendar()
            endRef?.current?.focus()
            return
        }
        if (pick === 'start' && start && end && iso <= end) {
            // moving the first day of a complete range
            onChange({ start: iso, end })
            closeCalendar()
            startRef?.current?.focus()
            return
        }
        // first click, or a click before the start
        onChange({ start: iso, end: '' })
        setPick('end')
        requestFocus(iso)
    }

    const shift = (n, focus) => {
        setView((previous) => nextMonth(previous, n))
        if (focus) requestFocus(focus)
    }

    const clear = () => {
        onChange({ start: '', end: '' })
        setPick('start')
    }

    const handleKeyDown = (event) => {
        if (event.key !== 'Escape' || !open) return
        event.preventDefault()
        event.stopPropagation()
        closeCalendar()
        const back = pick === 'end' ? endRef : startRef
        back?.current?.focus()
    }

    const rangeText = () => {
        const n = dayCount(start, end)
        const r = start === end ? formatDate(start, language) : formatRange(start, end, language)
        return n === 1 ? t('{{r}} · 1 day', { r }) : t('{{r}} · {{n}} days', { r, n })
    }
    const hint = !start
        ? t('Choose the first day')
        : !end || pick === 'end'
        ? end
            ? rangeText()
            : t('Choose the last day — the same one for a one-day event')
        : rangeText()

    const halfClass = (value, which) => `cev-half${value ? '' : ' ph'}${open && pick === which ? ' on' : ''}`

    return (
        <div onKeyDown={handleKeyDown}>
            <div className={`cev-range${open ? ' open' : ''}`}>
                <button
                    type="button"
                    ref={startRef}
                    className={halfClass(start, 'start')}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                    onClick={() => handleHalf('start')}
                >
                    <CalendarIcon />
                    <span>{start ? formatDate(start, language) : t('Start')}</span>
                </button>
                <ArrowRightIcon className="cev-arr" />
                <button
                    type="button"
                    ref={endRef}
                    className={halfClass(end, 'end')}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                    onClick={() => handleHalf('end')}
                >
                    <span>{end ? formatDate(end, language) : t('End')}</span>
                </button>
            </div>

            {open && (
                <DateRangeCalendar
                    labelledBy={labelledBy}
                    start={start}
                    end={end}
                    pick={pick}
                    view={view}
                    hint={hint}
                    focusIso={focusIso}
                    focusKey={focusKey}
                    onShift={shift}
                    onChoose={choose}
                    onClear={clear}
                    language={language}
                    t={t}
                />
            )}
        </div>
    )
}
